// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AppIcon, Button, ButtonGroup, IconButton, Modal, Spinner, Tooltip, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../api/proposalApi';
import { relationshipApi } from '../../../api/relationshipApi';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import type { BreakthroughProposalDetails, Proposal, ProposalSection } from '../../../types/proposal';
import {
  BREAKTHROUGH_ADDABLE_SECTION_KEYS,
  BREAKTHROUGH_SHARE_OPTIONAL_SECTION_KEYS,
} from '../../../types/proposal';
import { BreakthroughClientPreview } from './BreakthroughClientPreview';
import { renderBreakthroughSection } from './breakthroughSectionRenderer';
// The Breakthrough Report builder reuses the sectioned-builder shell styles
// verbatim (rail / content / facts-card / table / AI-row classes), including
// the generic view-mode toggle header, section-title header and read-only
// Client view document layout classes (BR-P5) — shared with
// QuoteBackedProposalBuilder / QuoteBackedClientPreview.
import './PensionProposalBuilder.css';

/** Human-readable labels for the "+" chooser (mirrors the PENSION rail). */
const ADDABLE_SECTION_LABELS: Record<string, string> = {
  BR_APPENDICES: 'Appendices',
};

/** Advisor's editable builder vs. the client's read-only document preview (BR-P5, RQ-BR8). */
type BuilderViewMode = 'advisor' | 'client';

type RailEntry = Readonly<{
  key: string;
  title: string;
  complete: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

/**
 * Pull the incomplete section keys out of the backend's
 * `SECTIONS_INCOMPLETE: ...` 422 message — tokens that match a known
 * section key are treated as the offending sections.
 */
function extractIncompleteSectionKeys(message: string, sections: ProposalSection[]): string[] {
  const known = new Set(sections.map((s) => s.sectionKey));
  const tokens = message.split(/[^A-Z_]+/).filter(Boolean);
  return [...new Set(tokens.filter((t) => known.has(t)))];
}

/**
 * Breakthrough Report Builder (docs/specs/breakthrough-report-prd.md §5):
 * the full-page sectioned builder for the `BREAKTHROUGH_REPORT` proposal
 * type. Unlike {@link PensionProposalBuilder} there is no quote to request —
 * the proposal (and its ~14 default sections) already exists by the time
 * this page loads (created directly from the Proposals tab's "New Proposal →
 * Breakthrough Report" action, with no "who/what" dialog), so the shell is a
 * simpler rail + content-pane dispatch with no synthetic quote steps and no
 * "new" (id-less) mode.
 *
 * Route: `/relationships/:profileId/proposals/breakthrough-builder/:proposalId`.
 */
export function BreakthroughReportBuilder() {
  const { profileId, proposalId } = useParams<{ profileId: string; proposalId?: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [sections, setSections] = useState<ProposalSection[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [viewMode, setViewMode] = useState<BuilderViewMode>('advisor');
  const { showToast } = useToast();

  // Relationship (profile) display name for the breadcrumb trail — mirrors
  // PensionProposalBuilder's fetch exactly.
  const [relationshipName, setRelationshipName] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipApi
      .getRelationship(profileId)
      .then((r) => {
        if (!cancelled) setRelationshipName(r.relationshipName || r.profileName || null);
      })
      .catch(() => {
        if (!cancelled) setRelationshipName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      ...(profileId && relationshipName
        ? [{ label: relationshipName, to: `/relationships/${profileId}` }]
        : []),
      {
        label: 'Proposals',
        to: profileId ? `/relationships/${profileId}?tab=proposals` : undefined,
      },
    ]);
    setBackTo(profileId ? `/relationships/${profileId}?tab=proposals` : null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [profileId, relationshipName, setBreadcrumbItems, setBackTo]);

  useEffect(() => {
    if (!proposalId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([proposalApi.getProposal(proposalId), proposalApi.getSections(proposalId)])
      .then(([loadedProposal, loadedSections]) => {
        if (cancelled) return;
        setProposal(loadedProposal);
        setSections(loadedSections);
        setActiveKey(
          (prev) =>
            prev ?? loadedSections.find((s) => !s.complete)?.sectionKey ?? loadedSections[0]?.sectionKey ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load this proposal');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const handleSectionSaved = useCallback((updated: ProposalSection) => {
    setSections((prev) => prev.map((s) => (s.sectionKey === updated.sectionKey ? updated : s)));
  }, []);

  const handleSectionRemoved = useCallback(
    (sectionKey: string) => {
      setSections((prev) => prev.filter((s) => s.sectionKey !== sectionKey));
      setActiveKey((prev) => {
        if (prev !== sectionKey) return prev;
        const remaining = sections.filter((s) => s.sectionKey !== sectionKey);
        return remaining[0]?.sectionKey ?? null;
      });
    },
    [sections],
  );

  /**
   * Persists a report-level detail change (linked quote / embedded
   * scenarios / recommended funds / report intro). The backend upserts the
   * WHOLE detail row on every write, so this merges the partial change onto
   * the proposal's current `details` before PUTting — a naive partial PUT
   * would null out the other fields server-side.
   */
  const handleUpdateDetails = useCallback(
    async (partial: Partial<BreakthroughProposalDetails>) => {
      if (!proposal) return;
      const merged = { ...((proposal.details ?? {}) as BreakthroughProposalDetails), ...partial };
      const updated = await proposalApi.updateProposal(proposal.id, { details: merged });
      setProposal(updated);
    },
    [proposal],
  );

  const railEntries = useMemo<RailEntry[]>(
    () => sections.map((s) => ({ key: s.sectionKey, title: s.title, complete: s.complete })),
    [sections],
  );

  const availableToAdd = useMemo(
    () =>
      proposal
        ? BREAKTHROUGH_ADDABLE_SECTION_KEYS.filter((key) => !sections.some((s) => s.sectionKey === key))
        : [],
    [proposal, sections],
  );

  const handleAddSection = useCallback(
    async (key: string) => {
      if (!proposalId) return;
      setAddMenuOpen(false);
      setAddingKey(key);
      try {
        const created = await proposalApi.addSection(proposalId, key);
        setSections((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
        setActiveKey(created.sectionKey);
      } catch {
        // Best-effort — the rail simply doesn't gain the section; the
        // advisor can retry from the "+" menu.
      } finally {
        setAddingKey(null);
      }
    },
    [proposalId],
  );

  const activeSection = useMemo(
    () => sections.find((s) => s.sectionKey === activeKey) ?? null,
    [sections, activeKey],
  );

  // Share gate: every loaded section must be complete except the addable
  // Appendices (PRD §4 — every default-created BR section is required).
  const shareable = useMemo(
    () =>
      sections.length > 0 &&
      sections.every((s) => BREAKTHROUGH_SHARE_OPTIONAL_SECTION_KEYS.has(s.sectionKey) || s.complete),
    [sections],
  );

  const handleShare = useCallback(async () => {
    if (!proposalId || sharing) return;
    setSharing(true);
    try {
      await proposalApi.shareProposal(proposalId);
      setShareConfirmOpen(false);
      showToast('Breakthrough Report shared with the client', 'success');
      navigate(profileId ? `/relationships/${profileId}?tab=proposals` : '/relationships');
    } catch (err) {
      setShareConfirmOpen(false);
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const message = apiErrorMessage(err, 'Failed to share the Breakthrough Report');
      if (status === 422 && message.startsWith('SECTIONS_INCOMPLETE')) {
        const keys = extractIncompleteSectionKeys(message, sections);
        const titles = keys.map((key) => sections.find((s) => s.sectionKey === key)?.title ?? key);
        if (keys.length > 0) setActiveKey(keys[0]);
        showToast(
          titles.length > 0
            ? `Complete these sections before sharing: ${titles.join(', ')}`
            : 'Complete all required sections before sharing.',
          'error',
        );
      } else if (status === 409) {
        showToast('This proposal is no longer a draft — it may already have been sent.', 'error');
      } else {
        showToast(message, 'error');
      }
    } finally {
      setSharing(false);
    }
  }, [proposalId, sharing, showToast, navigate, profileId, sections]);

  if (loading) {
    return (
      <div className="pension-builder" data-testid="breakthrough-builder-loading">
        <div className="pension-builder__card" style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="pension-builder">
        <div className="pension-builder__card pension-builder__error" data-testid="breakthrough-builder-error">
          {error ?? 'Failed to load this proposal'}
        </div>
      </div>
    );
  }

  return (
    <div className="pension-builder" data-testid="breakthrough-builder">
      <div className="pension-builder__view-toggle-header">
        <ButtonGroup aria-label="Advisor view or client view" data-testid="breakthrough-builder-view-toggle">
          <Button
            variant={viewMode === 'advisor' ? 'filled' : 'outlined'}
            onClick={() => setViewMode('advisor')}
            aria-pressed={viewMode === 'advisor'}
            data-testid="breakthrough-builder-view-toggle-advisor"
          >
            Advisor view
          </Button>
          <Button
            variant={viewMode === 'client' ? 'filled' : 'outlined'}
            onClick={() => setViewMode('client')}
            aria-pressed={viewMode === 'client'}
            data-testid="breakthrough-builder-view-toggle-client"
          >
            Client view
          </Button>
        </ButtonGroup>
      </div>

      {viewMode === 'client' ? (
        <BreakthroughClientPreview
          proposalId={proposalId ?? ''}
          profileId={profileId}
          proposal={proposal}
          sections={sections}
          onSaved={handleSectionSaved}
          onRemoved={handleSectionRemoved}
          onUpdateDetails={handleUpdateDetails}
          onBackToEditing={() => setViewMode('advisor')}
        />
      ) : (
      <div className="pension-builder__body">
        <nav className="pension-builder__rail" aria-label="Breakthrough Report sections">
          {railEntries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={[
                'pension-builder__rail-item',
                entry.key === activeKey ? 'pension-builder__rail-item--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setActiveKey(entry.key)}
              data-testid={`breakthrough-builder-rail-item-${entry.key}`}
            >
              <span>{entry.title}</span>
              {entry.complete && (
                <span
                  className="pension-builder__rail-tick"
                  data-testid={`breakthrough-builder-rail-tick-${entry.key}`}
                >
                  <AppIcon name="tick circle" size={16} color="green" />
                </span>
              )}
            </button>
          ))}

          {availableToAdd.length > 0 && (
            <div className="pension-builder__add-section">
              <IconButton
                variant="square-outlined"
                size="sm"
                onClick={() => setAddMenuOpen((v) => !v)}
                icon={<AppIcon name="add" />}
                aria-label="Add section"
                aria-haspopup="menu"
                aria-expanded={addMenuOpen}
                data-testid="breakthrough-builder-add-section-btn"
              />
              {addMenuOpen && (
                <div className="pension-builder__add-menu" role="menu" data-testid="breakthrough-builder-add-menu">
                  {availableToAdd.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="menuitem"
                      className="pension-builder__add-menu-item"
                      onClick={() => handleAddSection(key)}
                      disabled={addingKey === key}
                      data-testid={`breakthrough-builder-add-menu-item-${key}`}
                    >
                      {ADDABLE_SECTION_LABELS[key] ?? key}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="pension-builder__rail-share">
            {shareable ? (
              <Button
                variant="filled"
                onClick={() => setShareConfirmOpen(true)}
                disabled={sharing}
                loading={sharing}
                data-testid="breakthrough-builder-share-btn"
              >
                Share With Client
              </Button>
            ) : (
              <Tooltip
                content="Complete all required sections first"
                trigger={
                  <Button variant="filled" disabled data-testid="breakthrough-builder-share-btn">
                    Share With Client
                  </Button>
                }
              />
            )}
          </div>
        </nav>

        <div className="pension-builder__content">
          {activeSection && (
            <>
              <h2 className="pension-builder__section-title">{activeSection.title}</h2>
              {renderBreakthroughSection(
                activeSection,
                proposalId ?? '',
                profileId,
                proposal,
                handleSectionSaved,
                handleSectionRemoved,
                handleUpdateDetails,
              )}
            </>
          )}
        </div>
      </div>
      )}

      <Modal open={shareConfirmOpen} onClose={() => setShareConfirmOpen(false)} size="medium">
        <Modal.Header
          icon="send"
          title="Share With Client"
          onClose={() => setShareConfirmOpen(false)}
        />
        <Modal.Content>
          <p data-testid="breakthrough-builder-share-copy">
            This freezes the report and sends it to the client to read and accept.
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{
            label: 'Cancel',
            onClick: () => setShareConfirmOpen(false),
            disabled: sharing,
          }}
          primaryAction={{
            label: 'Share With Client',
            onClick: () => {
              void handleShare();
            },
            disabled: sharing,
            loading: sharing,
          }}
        />
      </Modal>
    </div>
  );
}

export default BreakthroughReportBuilder;
